/**
 * AG-UI Client wrapper using @ag-ui/client HttpAgent
 * 
 * Leverages AG-UI protocol library for standard, compliant communication
 */

import { RunAgentInput, BaseEvent } from '@ag-ui/core';
import { Observable } from 'rxjs';
import { getApiConfig } from '../../../api/config';
import { getFirebaseAuth } from '../../../config/firebase';
import { authTokenCache } from '../../../utils/authTokenCache';

/**
 * Get authentication token from Firebase Auth with caching
 * 
 * Uses authTokenCache to avoid repeated Firebase API calls and ensure
 * tokens are always fresh (Firebase tokens expire after ~1 hour).
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const auth = getFirebaseAuth();
    
    if (!auth) {
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        // eslint-disable-next-line no-console
        console.warn('[AGUIClient] Firebase Auth is not initialized');
      }
      return null;
    }
    
    if (!auth.currentUser) {
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        // eslint-disable-next-line no-console
        console.warn('[AGUIClient] No authenticated user found');
      }
      return null;
    }
    
    // Use cached token instead of fetching new one every time
    const token = await authTokenCache.getToken(auth);
    
    if (!token) {
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        // eslint-disable-next-line no-console
        console.warn('[AGUIClient] Failed to get ID token');
      }
      return null;
    }
    
    return token;
  } catch (error) {
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      // eslint-disable-next-line no-console
      console.error('[AGUIClient] Failed to get auth token:', error);
    }
    return null;
  }
}

/**
 * AG-UI Client wrapper using @ag-ui/client HttpAgent
 */
export class AGUIClient {
  private token: string | null = null;

  constructor() {
    // Token will be fetched asynchronously when needed
    this.token = null;
  }

  /**
   * Set authentication token
   */
  setToken(token: string | null): void {
    this.token = token;
  }

  /**
   * Send AG-UI request and stream events using HttpAgent
   * 
   * Uses standard AG-UI protocol library (@ag-ui/client HttpAgent) to ensure
   * protocol compliance and leverage maintained, standard code.
   * 
   * HttpAgent.runAgent() accepts RunAgentParameters (messages, threadId, tools, context, etc.)
   * and internally constructs the full RunAgentInput to send to the backend.
   * 
   * Returns Observable<BaseEvent> for compatibility with existing code.
   */
  runAgent(
    input: RunAgentInput,
    signal?: AbortSignal
  ): Observable<BaseEvent> {
    // Using HttpAgent.runAgent() with AG-UI protocol

    const config = getApiConfig();
    const endpoint = `${config.baseURL}/api/v1/ai-assist/agui`;

    // Send RunAgentInput directly via fetch to ensure messages are included
    // Parse SSE events according to AG-UI protocol format
    return new Observable<BaseEvent>((subscriber) => {
      let abortHandler: (() => void) | null = null;

      // Get token asynchronously from Firebase Auth with caching
      getAuthToken()
        .then((token) => {
          if (!token) {
            subscriber.error(new Error('Authentication token not found. Please log in again.'));
            return;
          }

          // Ensure token is set and agent is updated if token changed
          if (token !== this.token) {
            this.setToken(token);
          }

          // Continue with request processing
          this._processRequest(subscriber, input, token, endpoint, signal, (handler) => {
            abortHandler = handler;
          });
        })
        .catch((error) => {
          subscriber.error(new Error(`Authentication failed: ${error.message}`));
        });

      // Return cleanup function for Observable
      return () => {
        if (abortHandler && signal) {
          signal.removeEventListener('abort', abortHandler);
        }
      };
    });
  }

  /**
   * Internal method to process the request after token is obtained
   * @private
   */
  private _processRequest(
    subscriber: any,
    input: RunAgentInput,
    token: string,
    endpoint: string,
    signal?: AbortSignal,
    setAbortHandler?: (handler: (() => void) | null) => void
  ): void {

      // Extract thread_id and run_id - handle both camelCase and snake_case
      // TypeScript types use snake_case, but we need camelCase for JSON
      // Also handle the case where validation might have transformed the structure
      const threadId = 
        (input as any).thread_id || 
        (input as any).threadId ||
        (input as any).thread_id_value ||
        undefined;
      const runId = 
        (input as any).run_id || 
        (input as any).runId ||
        (input as any).run_id_value ||
        undefined;

      // Validate required fields - MUST be strings
      if (!threadId || typeof threadId !== 'string' || threadId.trim() === '') {
        const errorMsg = `thread_id/threadId is required and must be a non-empty string in RunAgentInput. Got: ${JSON.stringify(threadId)}`;
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
          // eslint-disable-next-line no-console
          console.error('[AGUIClient]', errorMsg);
        }
        subscriber.error(new Error(errorMsg));
        return;
      }
      if (!runId || typeof runId !== 'string' || runId.trim() === '') {
        const errorMsg = `run_id/runId is required and must be a non-empty string in RunAgentInput. Got: ${JSON.stringify(runId)}`;
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
          // eslint-disable-next-line no-console
          console.error('[AGUIClient]', errorMsg);
        }
        subscriber.error(new Error(errorMsg));
        return;
      }

      // Prepare request body in AG-UI protocol format (camelCase)
      // Backend Pydantic model expects camelCase in JSON but uses snake_case internally
      // Explicitly construct to ensure all fields are included
      const requestBody: {
        threadId: string;
        runId: string;
        messages: any[];
        tools: any[];
        context: any[];
        state: any;
        forwardedProps: any;
        parentRunId?: string;
      } = {
        threadId: String(threadId).trim(),
        runId: String(runId).trim(),
        messages: Array.isArray(input.messages) ? input.messages : [],
        tools: Array.isArray(input.tools) ? input.tools : [],
        context: Array.isArray(input.context) ? input.context : [],
        state: input.state && typeof input.state === 'object' ? input.state : {},
        forwardedProps: input.forwardedProps && typeof input.forwardedProps === 'object' ? input.forwardedProps : {},
      };

      // Add parentRunId only if it exists
      const parentRunId = (input as any).parent_run_id || (input as any).parentRunId;
      if (parentRunId && typeof parentRunId === 'string' && parentRunId.trim() !== '') {
        requestBody.parentRunId = parentRunId.trim();
      }

      // Final validation - ensure requestBody has required fields
      if (!requestBody.threadId || !requestBody.runId) {
        const errorMsg = `Request body is missing required fields. threadId: ${requestBody.threadId}, runId: ${requestBody.runId}`;
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
          // eslint-disable-next-line no-console
          console.error('[AGUIClient]', errorMsg);
        }
        subscriber.error(new Error(errorMsg));
        return;
      }


    // Handle abort signal
    let localAbortHandler: (() => void) | null = null;
    if (signal) {
      localAbortHandler = () => {
        subscriber.complete();
      };
      signal.addEventListener('abort', localAbortHandler);
      if (setAbortHandler) {
        setAbortHandler(localAbortHandler);
      }
    }

    // Send RunAgentInput directly as JSON in request body
    // Note: Authorization header is used by API Gateway for firebase_jwt validation
    // X-Flowrithm-User-Token is forwarded through Gateway to ai-assist-service
    // so the agent can use the real Firebase token (not the Gateway service account token)
    fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${token}`, // For API Gateway firebase_jwt validation
          'X-Flowrithm-User-Token': token, // Forwarded to ai-assist-service for agent use
        },
        body: JSON.stringify(requestBody),
        signal,
      })
        .then(async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }

          if (!response.body) {
            throw new Error('Response body is null');
          }

          // Parse SSE events (AG-UI protocol format: "data: {json}\n\n")
          const reader = response.body.getReader();
          const textDecoder = new TextDecoder();
          let buffer = '';
          let eventCount = 0;

          try {
            while (true) {
              const { done, value } = await reader.read();
              
              if (done) {
                // Process any remaining buffer
                if (buffer.trim()) {
                  const lines = buffer.split('\n');
                  for (const line of lines) {
                    const trimmed = line.trim();
                    if (trimmed.startsWith('data: ')) {
                      try {
                        const jsonData = trimmed.slice(6);
                        if (jsonData) {
                          const event = JSON.parse(jsonData) as BaseEvent;
                          eventCount++;
                          if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
                            // eslint-disable-next-line no-console
                            console.log(`[AGUIClient] Parsed event #${eventCount}:`, event.type);
                          }
                          subscriber.next(event);
                        }
                      } catch (e) {
                        // Skip invalid JSON lines
                        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
                          // eslint-disable-next-line no-console
                          console.warn('[AGUIClient] Failed to parse SSE event:', trimmed, e);
                        }
                      }
                    }
                  }
                }
                if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
                  // eslint-disable-next-line no-console
                  console.log(`[AGUIClient] Stream completed. Total events: ${eventCount}`);
                }
                subscriber.complete();
                break;
              }

              buffer += textDecoder.decode(value, { stream: true });
              
              // Parse complete SSE events from buffer (separated by \n\n)
              while (buffer.includes('\n\n')) {
                const eventEnd = buffer.indexOf('\n\n');
                const eventBlock = buffer.slice(0, eventEnd);
                buffer = buffer.slice(eventEnd + 2);
                
                // Parse event block
                const lines = eventBlock.split('\n');
                for (const line of lines) {
                  const trimmed = line.trim();
                  if (trimmed.startsWith('data: ')) {
                    try {
                      const jsonData = trimmed.slice(6);
                      if (jsonData) {
                        const event = JSON.parse(jsonData) as BaseEvent;
                        eventCount++;
                        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
                          // eslint-disable-next-line no-console
                          console.log(`[AGUIClient] Parsed event #${eventCount}:`, event.type);
                        }
                        subscriber.next(event);
                      }
                    } catch (e) {
                      // Skip invalid JSON lines
                      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
                        // eslint-disable-next-line no-console
                        console.warn('[AGUIClient] Failed to parse SSE event:', trimmed, e);
                      }
                    }
                  }
                }
              }
            }
          } catch (error) {
            if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
              // eslint-disable-next-line no-console
              console.error('[AGUIClient] Stream error:', error);
            }
            subscriber.error(error);
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
              // eslint-disable-next-line no-console
              console.error('[AGUIClient] Request error:', error);
            }
            subscriber.error(error);
          } else {
            subscriber.complete();
          }
        });
  }
}

// Singleton instance
export const aguiClient = new AGUIClient();
